/** venture/kit13defs.ts — 14단계 「투자확인서 발급」 방식별 필요 정보·서류 정의 (지도사 스펙 2026-09-24)
 *  제목·설명글·링크·민감 여부는 지도사가 자료수집 모듈에서 실제 쓰던 포털 항목을 그대로 따른다
 *  (예: 「주주 계좌 → 법인 계좌 이체내역」 · 「공증된 이사회 회의록 (또는 주주전원 서면결의서)」 · 「법인통장 사본」).
 *  from: 앞단계 연동 출처 — 있으면 자동으로 채워지고, 없으면 직접 입력/업로드 또는 자료수집 링크로 받는다. */
import type { CertMode, StoreKey, CapitalRound } from './types';

export const KBAN_URL = 'https://tax.kban.or.kr/';   // 투자확인서 발급시스템 (엔젤투자지원센터)

/** text = 한 줄 · memo = 여러 줄(투자자 인적사항) · members = 조합원 인적사항(마스터 자동) · file = 업로드 */
export type KitKind = 'text' | 'memo' | 'members' | 'file';
/** 앞단계 연동 출처 */
export interface KitFrom {
  kind: 'text' | 'file' | 'doc' | 'collect' | 'master';
  label: string;                        // 표시 태그 (예: 6단계 연동)
  store?: StoreKey; key?: string;       // text/file — 단계 저장소
  docKey?: string; bond?: string;       // doc — 생성 문서
  signed?: boolean;                     // doc — 서명(날인)본이어야 완료로 친다
  collectKey?: string;                  // collect — 1단계 GP 답변 매핑키
}
export interface KitItem {
  slot: string; label: string; kind: KitKind;
  from?: KitFrom; hint?: string; link?: string;
  desc?: string;                        // 자료수집 포털 설명글
  sensitive?: boolean;                  // 포털 민감 항목 (주민번호·비밀번호 등)
  tail?: boolean;                       // 발급 후 보관 항목 — 자료수집 요청 대상에서 제외
}

const ID_CORP: KitItem = { slot: 'idpw', label: '투자확인서 발급시스템 ID, PW', kind: 'text', hint: '법인 명의로 가입 후 ID, PW', link: KBAN_URL, sensitive: true,
  desc: '법인 명의로 가입 후, ID, PW를 입력해주세요.' };
const INVESTORS = (desc: string): KitItem => ({ slot: 'investors', label: '투자자(주주) 이름, 주소, 주민번호, 휴대폰번호', kind: 'memo', hint: '한 사람에 한 줄', sensitive: true, desc,
  from: { kind: 'master', label: '마스터(대표자) 자동' } });
const CORP_BOOK: KitItem = { slot: 'corpbook', label: '법인통장 사본', kind: 'file', desc: '자본금 입금을 받은 법인통장이어야 합니다.' };
const TAIL: KitItem = { slot: 'issued', label: '투자확인서 발급본', kind: 'file', hint: '발급 완료 후 PDF 보관', tail: true };

/** 증자 회차 요약 — 설명글에 넣는다 (예: "2026년 40,000,000원 증자") */
export function roundsText(rounds: CapitalRound[]): string {
  const rs = (rounds || []).filter((r) => r.date || r.amount);
  if (!rs.length) return '자본금 증자';
  return rs.map((r) => (r.date ? r.date.slice(0, 4) + '년 ' : '') + (r.amount ? r.amount + '원 ' : '') + '증자').join(', ');
}

/** 법인등기부등본 — 발급 신청에 항상 첨부. 9단계(전환사채 등기 후) 업로드를 기본으로 잇고, 더 새 것이 필요하면 직접 올린다 */
const REG_BOOK = (coId: string, label: string, hint?: string): KitItem => ({ slot: 'regcb', label, kind: 'file', hint,
  from: { kind: 'file', label: '9단계 연동', store: 's9', key: coId + '_regcb' }, desc: '가장 최근에 발급한 법인등기부등본(말소사항 포함)입니다.' });

export function kitDefs(mode: CertMode | '', rounds: CapitalRound[], coId: string): KitItem[] {
  const rt = roundsText(rounds);
  if (mode === 'capital_inc') return [
    ID_CORP,
    REG_BOOK(coId, '법인등기부등본 (증자 등기 후)', '증자 등기가 반영된 최신본이 아니면 새로 발급해 직접 업로드'),
    { slot: 'capin', label: '주주 계좌 → 법인 계좌 이체내역', kind: 'file', desc: rt + '에 관한 내역입니다.' },
    CORP_BOOK,
    INVESTORS(rt + '에 참여한 분들에 대한 정보입니다.'),
    { slot: 'minutes', label: '공증된 이사회 회의록 (또는 주주전원 서면결의서)', kind: 'file', sensitive: true, desc: '자본금 증자 당시 법무사사무소와 소통하신 자료입니다.' },
    TAIL,
  ];
  if (mode === 'capital_new') return [
    { ...ID_CORP, desc: '법인으로 가입해주세요.' },
    REG_BOOK(coId, '법인등기부등본 (설립 등기)'),
    { slot: 'capin', label: '발기인 계좌 → 법인 계좌 이체내역', kind: 'file', desc: '법인 설립 자본금을 발기인 계좌에서 법인 계좌로 보낸 내역입니다.' },
    { slot: 'founderin', label: '타 주주 계좌 → 발기인 계좌 이체내역', kind: 'file', desc: '발기인 외 주주가 발기인에게 출자금을 보낸 내역(이체확인증 등)입니다.' },
    CORP_BOOK,
    INVESTORS('법인 설립에 참여한 주주들에 대한 정보입니다.'),
    { slot: 'founders', label: '발기인 총회 의사록', kind: 'file', desc: '법인 설립 시, 법무사님과 주고받으신 서류입니다.' },
    TAIL,
  ];
  if (mode === 'cb_fund') return [
    { slot: 'idpw', label: '투자확인서 발급시스템 ID, PW', kind: 'text', hint: '1단계 엔젤투자교육 사이트 ID·PW 와 같음', link: KBAN_URL, sensitive: true,
      desc: '엔젤투자교육 사이트와 같은 ID, PW 입니다.', from: { kind: 'collect', label: '1단계 연동', collectKey: 'eduIdPw' } },
    REG_BOOK(coId, '법인등기부등본 (전환사채 등기 후)'),
    { slot: 'contract', label: '전환사채 인수계약서 + 조합원 전원 동의서 (조합 인수 · 서명본)', kind: 'file', hint: '8단계 계약서 — 특수관계인 투자 동의서(벤처투자촉진법 §14②) 포함 · GP·조합원 전원 서명',
      from: { kind: 'doc', label: '8단계 계약서 · 서명', bond: 'bondcontract', signed: true } },
    { slot: 'fundout', label: '조합 계좌 → 법인 계좌 이체내역', kind: 'file', desc: '조합통장에서 법인통장으로 전환사채 인수대금을 보낸 내역(이체확인증 등)입니다.' },
    { slot: 'memberin', label: '조합원 계좌 → 조합 계좌 이체내역 (출자이행 증빙)', kind: 'file', desc: '조합원 각자가 조합통장에 출자금을 보낸 내역입니다.',
      from: { kind: 'file', label: '6단계 연동', store: 's6', key: 'capproof' } },
    { slot: 'fundbook', label: '조합통장 사본', kind: 'file', desc: '전환사채 인수대금을 보낸 조합통장입니다.' },
    { slot: 'members', label: '조합원 이름, 주소, 주민번호, 휴대폰번호', kind: 'members', hint: '마스터 조합원 정보 자동 · 출자증표 열어 대조', sensitive: true },
    TAIL,
  ];
  if (mode === 'cb_direct') return [
    ID_CORP,
    { slot: 'cancel', label: '전환사채 말소등기 및 재등기 후 법인등기부등본', kind: 'file', hint: '법무사 말소·재등기 완료 후', desc: '조합 인수 전환사채의 말소등기와 대표자 인수분 재등기가 반영된 등기부등본입니다.' },
    { slot: 'dcontract', label: '전환사채 인수계약서 (대표자 재인수 · 서명본)', kind: 'file', hint: '자동 생성 → 대표자 서명',
      from: { kind: 'doc', label: '자동 생성 · 서명', bond: 'bondceo', signed: true } },
    { slot: 'dpaid', label: '대표자 계좌 → 법인 계좌 이체내역', kind: 'file', desc: '대표자가 전환사채 인수대금을 법인통장으로 보낸 내역(이체확인증 등)입니다.' },
    CORP_BOOK,
    INVESTORS('전환사채를 인수한 대표자(투자자)에 대한 정보입니다.'),
    TAIL,
  ];
  return [];
}
